import type { StaffAnalyticsEventRow } from "@lafayette/schemas";
import type { ColumnDef } from "@tanstack/react-table";
import { createContext, useContext, useMemo, useRef } from "react";

import { Button } from "../../ui/button";
import { cn } from "../../ui/cn";
import { DataTable } from "../../ui/data-table";
import { eventStatusOf } from "../event-sentences";
import { eventTime, propertyLine } from "./events-model";
import { StatusPill } from "./StatusPill";

// The event table (Paper "Event table"): the time, the event, its properties on one line, and the status. The event name is a real button that
// opens the panel (the row also reacts to a click for the pointer). The row of the open event is marked in words for a screen reader
// (`aria-current`) and with a bar and a tint for the eye.

/** The event that is open, read by the buttons of the table. It is a context so the column definitions do not depend on it: a column that changes at every open remounts every button (`flexRender`), and the focus then has no button to return to. */
const OpenEventContext = createContext<string | null>(null);

function EventButton({
  uuid,
  name,
  onOpen,
}: Readonly<{ uuid: string; name: string; onOpen: (uuid: string) => void }>) {
  const openUuid = useContext(OpenEventContext);
  return (
    <Button
      type="button"
      variant="ghost"
      aria-current={uuid === openUuid ? "true" : undefined}
      onClick={(clicked) => {
        clicked.stopPropagation();
        onOpen(uuid);
      }}
      className="h-auto min-h-0 justify-start p-0 font-mono text-[13px] font-bold hover:bg-transparent compact:h-auto"
    >
      {name}
    </Button>
  );
}

export function EventTable({
  rows,
  openUuid,
  tz,
  now,
  onOpen,
  isLoading,
  emptyState,
}: Readonly<{
  rows: readonly StaffAnalyticsEventRow[];
  openUuid: string | null;
  tz: string;
  now: string;
  onOpen: (uuid: string) => void;
  isLoading: boolean;
  emptyState: React.ReactNode;
}>) {
  // `onOpen` is a new function at every render of the tab; the columns read the latest one through a ref and so stay the same.
  const onOpenRef = useRef(onOpen);
  onOpenRef.current = onOpen;
  const columns = useMemo<ColumnDef<StaffAnalyticsEventRow>[]>(
    () => [
      {
        id: "time",
        header: "Time",
        enableSorting: false,
        meta: { className: "w-28 whitespace-nowrap text-[13px] text-muted-foreground" },
        cell: ({ row }) => eventTime(row.original.timestamp, tz, now),
      },
      {
        id: "event",
        header: "Event",
        enableSorting: false,
        meta: { className: "w-48", rowHeader: true },
        cell: ({ row }) => (
          <EventButton
            uuid={row.original.uuid}
            name={row.original.event}
            onOpen={(uuid) => onOpenRef.current(uuid)}
          />
        ),
      },
      {
        id: "properties",
        header: "Properties",
        enableSorting: false,
        meta: { className: "min-w-0 font-mono text-xs text-muted-foreground" },
        cell: ({ row }) => (
          <span className="line-clamp-1 [overflow-wrap:anywhere]">
            {propertyLine(row.original.properties)}
          </span>
        ),
      },
      {
        id: "status",
        header: "Status",
        enableSorting: false,
        meta: { className: "w-[72px]", headClassName: "text-right" },
        cell: ({ row }) => (
          <span className="flex justify-end">
            <StatusPill status={eventStatusOf(row.original.event)} />
          </span>
        ),
      },
    ],
    [tz, now],
  );
  return (
    <OpenEventContext.Provider value={openUuid}>
      <DataTable
        caption="Events of this customer, newest first"
        columns={columns}
        data={rows}
        getRowId={(row) => row.uuid}
        banded
        isLoading={isLoading}
        loadingRows={6}
        emptyState={emptyState}
        onRowClick={(row) => onOpen(row.uuid)}
        rowClassName={(row) =>
          cn(
            "cursor-pointer",
            row.uuid === openUuid
              ? "bg-accent shadow-[inset_3px_0_0_0_var(--color-primary)]"
              : undefined,
          )
        }
      />
    </OpenEventContext.Provider>
  );
}
